import { useState, useContext } from 'react'
import { Button, Chip, Grid, Typography, useTheme } from '@material-ui/core'
import { UserCommunityProfile, UserContext, UserContextType } from '../../UserContext'
import BottomFullButtonStandalone from '../../UIComponents/BottomFullButtonStandalone'
import clsx from 'clsx'
import useMuseStyles from '../../themes/useMuseStyles'
import { useCommunityFeature } from '../../services/useCommunityFeature'
import { AppContext, AppContextType } from '../../AppContext'
import CommunityProfileEdit from './CommunityProfileEdit'
import ConfirmationDialogContents from '../ConfirmationDialogContents'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'

type PageMode = 'view' | 'edit'

function CommunityProfileView({
    profile,
    headerText = 'Your Profile',
    readonly,
}: {
    profile: UserCommunityProfile
    headerText?: string
    readonly?: boolean
}) {
    const gclasses = useMuseStyles(useTheme())

    const { state, dispatch } = useContext(UserContext) as UserContextType
    const { dispatch: appDispatch } = useContext(AppContext) as AppContextType

    const communityFeature = useCommunityFeature()

    const [pageMode, set_pageMode] = useState<PageMode>('view')
    const [isDeleteProfileConfirmationDialogOpen, set_isDeleteProfileConfirmationDialogOpen] = useState(false)

    const onEditProfile = () => {
        // TODO: enter edit mode
        set_pageMode('edit')
    }

    const onDeleteProfile = () => {
        communityFeature
            .deleteProfile()
            .then((result) => {
                dispatch({
                    type: 'set_user_community_profile',
                    value: null,
                })
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'success',
                        message: 'Successfully deleted your profile!',
                    },
                })
            })
            .catch((e) => {
                appDispatch({
                    type: 'root_snackbar_history_append',
                    value: {
                        severity: 'error',
                        message: 'Failed to delete profile! Server returned error.',
                    },
                })
            })
    }

    if (pageMode === 'edit') {
        return (
            <CommunityProfileEdit
                existingProfile={profile}
                onComplete={() => {
                    set_pageMode('view')
                }}
            />
        )
    }

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                height: '100%',
                width: '100%',
            }}
        >
            <Grid container style={{ paddingLeft: '4rem', paddingRight: '4rem' }}>
                <Grid xs={12}>
                    <Typography variant="h1" style={{ textAlign: 'center', marginBottom: '48px' }}>
                        {headerText}
                    </Typography>
                </Grid>
                <Grid xs={12} sm={6} style={{ padding: '0 18px' }}>
                    <ProfileContentsLeftPanel profile={profile} />
                </Grid>
                <Grid xs={12} sm={6} style={{ padding: '0 18px' }}>
                    <ProfileContentsRightPanel profile={profile} />
                </Grid>
            </Grid>
            {!readonly && (
                <Grid container>
                    <Grid xs={12} style={{ margin: '1rem' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            className={clsx(
                                gclasses.roundedMuseButton,
                                gclasses.buttonColorDangerFilled,
                                gclasses.buttonSmall
                            )}
                            classes={{ label: gclasses.roundedMuseButtonLabel }}
                            onClick={() => {
                                if (isDeleteProfileConfirmationDialogOpen) {
                                    set_isDeleteProfileConfirmationDialogOpen(false)
                                    setTimeout(() => set_isDeleteProfileConfirmationDialogOpen(true), 250)
                                } else {
                                    set_isDeleteProfileConfirmationDialogOpen(true)
                                }
                            }}
                            disabled={communityFeature.deleteProfileInProgress}
                        >
                            {communityFeature.deleteProfileInProgress ? 'Deleting Profile...' : 'Delete Profile'}
                        </Button>
                    </Grid>
                    <Grid xs={12}>
                        <BottomFullButtonStandalone
                            className={clsx(gclasses.buttonColorPrimary)}
                            onClick={onEditProfile}
                            disableRipple
                        >
                            EDIT PROFILE
                        </BottomFullButtonStandalone>
                    </Grid>
                </Grid>
            )}

            {isDeleteProfileConfirmationDialogOpen && (
                <CrossPlatformDialog
                    frameName="Dialog:DeleteProfileConfirmationDialog"
                    onDefaultClose={() => set_isDeleteProfileConfirmationDialogOpen(false)}
                >
                    <ConfirmationDialogContents
                        message={`Are you sure you want to delete your profile?`}
                        onPositive={() => {
                            onDeleteProfile()
                        }}
                        onNegative={() => set_isDeleteProfileConfirmationDialogOpen(false)}
                        positiveButtonText="Yes"
                        negativeButtonText="Cancel"
                    />
                </CrossPlatformDialog>
            )}
        </div>
    )
}

function ProfileContentsLeftPanel({ profile }: { profile: UserCommunityProfile }) {
    const { state, dispatch } = useContext(UserContext) as UserContextType

    return (
        <>
            <ProfileTextView label="Name" value={profile.name || ''} />
            <ProfileTextView label="Bio" value={profile.bio} multiline={true} />
        </>
    )
}

function ProfileContentsRightPanel({ profile }: { profile: any }) {
    return (
        <>
            <ProfileBadgeView label="Genre" value={profile.genre} />

            <ProfileBadgeView label="Instrument" value={profile.instrument} />

            <ProfileBadgeView label="Role" value={profile.role} />
        </>
    )
}

function ProfileTextView({
    label,
    value = '',
    multiline = false,
}: {
    label: string
    value?: string
    multiline?: boolean
}) {
    return (
        <div style={{ marginBottom: 24 }}>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
                {label}
            </Typography>
            {multiline ? (
                <>
                    {value.split('\n').map((line, i) => {
                        return (
                            <Typography
                                key={`line-${i}`}
                                variant="body2"
                                style={{ marginBottom: '0.8rem', lineHeight: '18px' }}
                            >
                                {line}
                            </Typography>
                        )
                    })}
                </>
            ) : (
                <Typography variant="body2">{value || ''}</Typography>
            )}
        </div>
    )
}

/**
 * view/edit/insert/remove badges
 */
function ProfileBadgeView({ label, value = [] }: { label: string; value?: string[] }) {
    return (
        <div style={{ marginBottom: 24 }}>
            <Typography variant="body1" style={{ marginBottom: '8px' }}>
                {label}
            </Typography>
            {value.map((item, i) => {
                return (
                    <Chip
                        key={`chip-${i}`}
                        style={{
                            marginRight: 5,
                            marginBottom: 5,
                            borderColor: 'deepskyblue',
                            color: 'deepskyblue',
                            height: 26,
                        }}
                        label={item}
                        variant="outlined"
                    />
                )
            })}
        </div>
    )
}

export default CommunityProfileView
